import React, { useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Badge,
  IconButton,
  Spinner,
  Center,
  useColorModeValue,
  useToast,
  Select,
  SimpleGrid,
  Avatar,
  Spacer
} from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiShare2, FiBook } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import { useAuth } from '../contexts/AuthContext';
import { useComics } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';

const Comics: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    comics,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingComics,
    loadComics,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useComics();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Comics context already loads only comics, no filtering needed

  useEffect(() => {
    loadComics(user?.uid);
  }, [selectedGenre, loadComics, user?.uid]);

  // Refresh data when page comes into focus
  useEffect(() => {
    const handleFocus = () => {
      loadComics(user?.uid);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadComics, user?.uid]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handleComicClick = (comicId: string) => {
    navigate(`/comic/${comicId}`);
  };

  if (loading) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <VStack gap={4}>
            <Spinner size="xl" color="purple.500" />
            <Text color={labelColor}>Loading comics...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <VStack gap={4}>
            <Text color="red.500" fontSize="lg">Error loading comics</Text>
            <Text color={labelColor}>{error}</Text>
            <Button colorScheme="purple" onClick={() => loadComics(user?.uid)}>
              Try Again
            </Button>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box pt={0} mt={0} mb={0} pb={0} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto" w="100%">
      <VStack gap={6} align="stretch" w="100%" pt={0} mt={0}>
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <VStack align="start" gap={2}>
            <HStack gap={3}>
              <IconWrapper icon={FiBook} size={24} color="purple.500" />
              <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={titleColor}>
                Comics
              </Text>
            </HStack>
            <Text color={labelColor} fontSize={{ base: 'sm', md: 'md' }}>
              Discover amazing comics and graphic novels
            </Text>
          </VStack>
          
          <HStack gap={3}>
            <Select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              bg={cardBg}
              borderColor={borderColor}
              color={textColor}
              w={{ base: '150px', md: '200px' }}
            >
              <option value="all">All Genres</option>
              {GENRES.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* Comics Grid */}
        {comics.length === 0 ? (
          <Center py={20}>
            <VStack gap={4}>
              <IconWrapper icon={FiBook} size={48} color="gray.400" />
              <Text color={labelColor} fontSize="lg">No comics found</Text>
              <Text color={labelColor} fontSize="sm" textAlign="center">
                {selectedGenre === 'all' 
                  ? 'No comics have been uploaded yet.' 
                  : `No comics found in the ${selectedGenre} genre.`
                }
              </Text>
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 6 }} spacing={4}>
            {comics.map((comic) => (
              <Box
                key={comic.id}
                bg={cardBg}
                borderRadius="lg"
                overflow="hidden"
                shadow="sm"
                _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => handleComicClick(comic.id)}
              >
                <Box position="relative" w="100%" h="200px">
                  <CachedImage
                    src={
                      (typeof comic.assets?.coverFile === 'string' ? comic.assets.coverFile : '') ||
                      comic.poster ||
                      comic.thumbnail
                    }
                    alt={comic.title}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Badge
                    position="absolute"
                    top={2}
                    right={2}
                    colorScheme="purple"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    Comic
                  </Badge>
                </Box>
                
                <VStack align="start" p={3} gap={2}>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={titleColor}
                    noOfLines={2}
                    lineHeight="short"
                  >
                    {comic.title}
                  </Text>
                  
                  <Text fontSize="xs" color={labelColor} noOfLines={1}>
                    by {comic.uploader}
                  </Text>
                  
                  <HStack gap={2} wrap="wrap">
                    {comic.genres.slice(0, 2).map((genre) => (
                      <Badge
                        key={genre}
                        colorScheme="purple"
                        variant="subtle"
                        fontSize="xs"
                        px={2}
                        py={1}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </HStack>
                  
                  <HStack justify="space-between" w="100%">
                    <Text fontSize="xs" color={labelColor}>
                      {formatDate(comic.createdAt)}
                    </Text>
                    <HStack gap={1}>
                      <IconWrapper icon={FiThumbsUp} size={12} color={labelColor} />
                      <Text fontSize="xs" color={labelColor}>
                        {likeCounts[comic.id]?.likes || 0}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </VStack>
    </Box>
  );
};

export default Comics;
